import React, { useEffect, useState } from "react";
import API from "../services/api";
import { Container, Spinner, Alert } from "react-bootstrap";
import "./EmployeeIDCardPage.css";
import logo from "../assets/logo.png";
import { formatEmployeeId } from "../utils/idFormat";
import { buildEmployeeQrUrl } from "../utils/idCardQr";

const EmployeeIDCardPage = () => {
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState("");
  const resolvePhoto = (photo) => {
    if (!photo) return "https://via.placeholder.com/160";
    if (/^https?:\/\//i.test(photo) || photo.startsWith("data:") || photo.startsWith("blob:")) {
      return photo;
    }
    const base = process.env.REACT_APP_API_URL || "";
    return `${base}${photo}`;
  };

  const pickStart = (e) => e?.joiningDate || e?.approvedAt || e?.createdAt || null;

  const buildEmployeeId = (e) => formatEmployeeId(e);

  const fetchEmployee = async () => {
    try {
      const res = await API.get("/employee/my-idcard");
      setEmployee(res.data);
    } catch (err) {
      setError("Failed to load employee ID card");
    }
  };

  useEffect(() => {
    fetchEmployee();
  }, []);

  if (error) return <Alert variant="danger" className="mt-4">{error}</Alert>;
  if (!employee) return <div className="text-center mt-4"><Spinner animation="border" /></div>;

  if (employee.idCardStatus !== "generated") {
    return (
      <Alert className="mt-4" variant="warning">
        Your ID card is not yet generated.
      </Alert>
    );
  }

  const start = pickStart(employee);
  const designation = employee.designation || employee.employeeRole?.name || "Employee";
  const statusText = employee.approved === false ? "Inactive" : "Active";
  const qrUrl = buildEmployeeQrUrl(employee, 128);

  return (
    <Container className="mt-4 d-flex justify-content-center">
      <div className="idcard-preview">
        <div className="idcard-shell">
          <div className="idcard-head">
            <div className="idcard-headline">
              <span className="material-symbols-outlined">verified_user</span>
              <span>Official Digital Identity</span>
            </div>
          </div>
          <div className="idcard-photo">
            <div
              className="idcard-photo-inner"
              style={{ backgroundImage: `url(${resolvePhoto(employee.photo)})` }}
            />
          </div>
          <div className="idcard-body">
            <h3>{employee.fullName || "—"}</h3>
            <p className="idcard-role">{designation}</p>
            <div className="idcard-details">
              <div className="idcard-detail-row">
                <span>Employee ID</span>
                <strong>{buildEmployeeId(employee)}</strong>
              </div>
              <div className="idcard-detail-row">
                <span>Mobile</span>
                <strong>{employee.mobile || "—"}</strong>
              </div>
              <div className="idcard-detail-row">
                <span>Joined</span>
                <strong>{start ? new Date(start).toLocaleDateString() : "—"}</strong>
              </div>
              <div className="idcard-detail-row">
                <span>Department</span>
                <strong>{employee.department?.name || employee.departmentName || employee.department || "—"}</strong>
              </div>
              <div className="idcard-detail-row">
                <span>Role</span>
                <strong>{employee.employeeRole?.name || employee.role || "—"}</strong>
              </div>
              <div className="idcard-detail-row">
                <span>Status</span>
                <strong>{statusText}</strong>
              </div>
            </div>
            <div className={`idcard-verified ${employee.approved === false ? "danger" : ""}`}>
              {employee.approved === false ? "Terminated" : "Verified Employee"}
            </div>
            <div className="idcard-footer">
              <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." className="idcard-logo" />
              <div className="idcard-brand">Lotus Rice Mill Pvt. Ltd.</div>
              <div className="idcard-qr">
                <img
                  src={qrUrl}
                  alt={`QR for ${employee.fullName || "employee"} ID card`}
                  className="idcard-qr-image"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
          </div>
          <div className="idcard-stripe" />
        </div>
      </div>
    </Container>
  );
};

export default EmployeeIDCardPage;
